import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Dropdown,
  Icon,
  InputNumber,
  Menu,
  Modal,
  Row,
  Select,
  Table,
  Tooltip,
  notification,
} from "antd";
import moment from "moment";
import { FaCashRegister } from "react-icons/fa";
import { FiDelete } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import ButtonStandard from "../../../../components/utils/button";
import MenuIcon from "../../../../components/utils/table/icons/menu";
import { Container as MenuContainer } from "../../../../components/utils/table/menu/styles";
import TagStatus from "../../../../components/utils/tagStatus";
import { useFinancial } from "../../../../hooks/useFinancialReducer";
import { CardCustom, Title } from "./styles";

import NewFinancialTransactionsForm from "./form";

import { Creators as AppActions } from "../../../../store/ducks/app";

// Services
import {
  deleteFinancialTransactions,
  updateFinancialTransactionIssueToClose,
  updateFinancialTransactionIssueToOpen,
} from "../../../../services/financialTransactionsService";
import { getParameterItems } from "../../../../services/generalParameterService";
import {
  getTwoDecimalDigits,
  numberMask,
} from "../../../../services/helpersMethodsService";
import {
  getColumnDateWithBetweenSearchProps,
  getColumnNumberWithBetweenSearchProps,
  getColumnSearchProps,
} from "../../../../utils/tableUtils";
import { transformCamelCaseToSnakeCase } from "../../../../utils/stringUtils";
import InfoTooltip from "../../../../components/utils/infoTooltip";
import { downloadInvoiceFile } from "../../../../services/invoiceService";

const CLASS_ID_WITHOUT_EDITION = [];
const CODES_PARAMETERS = [4003, 4004, 4005, 4006];

// Extrai o nome do arquivo de: attachment; filename="abc.pdf"
function filenameFromDisposition(header, fallback) {
  const match = /filename="?([^";]+)"?/i.exec(header ?? "");
  return match ? match[1] : fallback;
}

// Com responseType "blob", o corpo do erro também vem como Blob: precisa ser lido como texto.
async function errorMessageFrom(error) {
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()).mensagem;
    } catch {
      // corpo não era JSON; cai no fallback abaixo
    }
  }
  return error.response
    ? `Falha ao baixar o arquivo (${error.response.status})`
    : "Não foi possível conectar ao servidor.";
}

const FinancialTransactions = () => {
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);
  const [parametersValue, setParametersValue] = useState([]);
  // Redux
  const {
    listFinancialTransaction: data,
    isLoadingListFinancialTransaction: isLoading,
    shouldShowFormFinancialTransaction,
    refreshListFinancialTransaction,
    openOrCloseDrawerFinancialTransactionForm,
    refreshListFinancialCashFlow,
    refreshListFinancialCosting,
    handleOpenModalSettled,
    handleOpenModalPartialPayment,
    handleSetFinancialTransactionDataToEdit,
    setTablePropertiesFinancialTransactions,
    tablePropertiesFinancialTransactions: {
      size,
      page,
      tableFilters,
      tableSorters,
      search,
    },
  } = useFinancial();
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);
  const Column = Table.Column;

  const dispatch = useDispatch();

  // Memo

  const isEnablePaymentMethod = useMemo(() => {
    return (
      parametersValue.find((parameter) => parameter.code === 4004) &&
      parametersValue.find((parameter) => parameter.code === 4004).value ===
        "true"
    );
  }, [parametersValue]);

  const isEnableFinancialIssue = useMemo(() => {
    return (
      parametersValue.find((parameter) => parameter.code === 4003) &&
      parametersValue.find((parameter) => parameter.code === 4003).value ===
        "true"
    );
  }, [parametersValue]);

  const isEnableFinancialTaxableDeductible = useMemo(() => {
    return (
      parametersValue.find((parameter) => parameter.code === 4005) &&
      parametersValue.find((parameter) => parameter.code === 4005).value ===
        "true"
    );
  }, [parametersValue]);

  const isEnableBankAccountFarm = useMemo(() => {
    return (
      parametersValue.find((parameter) => parameter.code === 4006) &&
      parametersValue.find((parameter) => parameter.code === 4006).value ===
        "true"
    );
  }, [parametersValue]);

  // Methods

  const getSortString = useCallback((sorter) => {
    let customSort = sorter;

    if (sorter && Object.entries(sorter).length > 0) {
      const { field } = sorter;

      const fieldS = field ? transformCamelCaseToSnakeCase(field) : null;

      customSort = {
        ...customSort,
        field: fieldS,
      };
    }

    return customSort;
  }, []);

  const getSearchString = useCallback((filters) => {
    let search = "";

    if (filters && Object.entries(filters).length > 0) {
      Object.entries(filters).forEach(([field, value]) => {
        if (value && value.length > 0) {
          const fieldS = field ? transformCamelCaseToSnakeCase(field) : null;
          if (fieldS === "value") {
            const operatorS =
              value[1] === "equal"
                ? "="
                : value[1] === "greaterThan"
                  ? ">"
                  : value[1] === "greaterOrEqualThan"
                    ? ">="
                    : value[1] === "lessThan"
                      ? "<"
                      : value[1] === "lessOrEqualThan"
                        ? "<="
                        : value[1] === "between"
                          ? "between"
                          : "=";
            search =
              search === ""
                ? `${fieldS} ${
                    operatorS === "between"
                      ? `${operatorS} ${value[0]} and ${value[2]}`
                      : `${operatorS} ${value[0]}`
                  }`
                : `${search};${fieldS} ${
                    operatorS === "between"
                      ? `${operatorS} ${value[0]} and ${value[2]}`
                      : `${operatorS} ${value[0]}`
                  }`;
          } else if (
            fieldS === "billing_date" ||
            fieldS === "expiration_date"
          ) {
            const operatorS =
              value[1] === "equal"
                ? "="
                : value[1] === "greaterThan"
                  ? ">"
                  : value[1] === "greaterOrEqualThan"
                    ? ">="
                    : value[1] === "lessThan"
                      ? "<"
                      : value[1] === "lessOrEqualThan"
                        ? "<="
                        : value[1] === "between"
                          ? "between"
                          : "=";

            search =
              search === ""
                ? `cast(${fieldS} as date) ${
                    operatorS === "between"
                      ? `${operatorS} cast('${moment(value[0]).format(
                          "YYYY-MM-DD",
                        )}' as date) and cast('${moment(value[2]).format(
                          "YYYY-MM-DD",
                        )}' as date)`
                      : `${operatorS} cast('${moment(value[0]).format(
                          "YYYY-MM-DD",
                        )}' as date)`
                  } `
                : `${search};cast(${fieldS} as date) ${
                    operatorS === "between"
                      ? `${operatorS} cast('${moment(value[0]).format(
                          "YYYY-MM-DD",
                        )}' as date) and cast('${moment(value[2]).format(
                          "YYYY-MM-DD",
                        )}' as date)`
                      : `${operatorS} cast('${moment(value[0]).format(
                          "YYYY-MM-DD",
                        )}' as date)`
                  }`;
          } else if (fieldS === "transaction_type" || fieldS === "origin") {
            if (fieldS === "transaction_type" && value === "all") {
              search =
                search === ""
                  ? `${fieldS} in ('expense', 'income')`
                  : `${search};${fieldS} in ('expense', 'income')`;
            } else {
              search =
                search === ""
                  ? `${fieldS} in (${value
                      .map((o) => (o = `'${o}'`))
                      .join(", ")})`
                  : `${search};${fieldS} in (${value
                      .map((o) => (o = `'${o}'`))
                      .join(", ")})`;
            }
          } else if (fieldS === "status") {
            if (value[0] === "conciliated") {
              search =
                search === ""
                  ? `conciliation_status = 'conciliated'`
                  : `${search};conciliation_status = 'conciliated'`;
            } else if (value[0] === "partial_conciliated") {
              search =
                search === ""
                  ? `conciliation_status = 'partial_conciliated'`
                  : `${search};conciliation_status = 'partial_conciliated'`;
            } else {
              search =
                search === ""
                  ? `is_settled = ${value[0] === "true"}`
                  : `${search};is_settled = ${value[0] === "true"}`;
            }
          } else {
            search =
              search === ""
                ? `upper(${fieldS}) like upper('%25${value}%25')`
                : `${search};upper(${fieldS}) like upper('%25${value}%25')`;
          }
        }
      });
    }

    return search !== "" ? search : "";
  }, []);

  const handleSearch = useCallback((selectedKeys, confirm, dataIndex) => {
    confirm();
  }, []);

  const handleReset = useCallback((clearFilters) => {
    clearFilters();
  }, []);

  const handleTableChange = useCallback(
    async (pagination = null, filters = null, sorter = null) => {
      if (pagination && filters && sorter) {
        let customSort = getSortString(sorter);
        let search = getSearchString(filters);

        setTablePropertiesFinancialTransactions({
          tableFilters: filters,
          tableSorters: sorter,
          size: pagination.pageSize,
          page: pagination.current,
          search,
        });

        refreshListFinancialTransaction(
          pagination.current,
          pagination.pageSize,
          customSort,
          search,
        );
      } else {
        setTablePropertiesFinancialTransactions({
          size: 10,
          page: 0,
          tableFilters: {},
          tableSorters: {},
          search: null,
        });
        refreshListFinancialTransaction();
      }
    },
    [
      getSearchString,
      getSortString,
      refreshListFinancialTransaction,
      setTablePropertiesFinancialTransactions,
    ],
  );

  const handleDelete = useCallback(
    async (id, record) => {
      async function executeDelete(id, deleteAllInstallments) {
        setIsLoadingRequest(true);
        try {
          await deleteFinancialTransactions({
            groupId,
            farmId,
            id,
            deleteAllInstallments,
          });
          refreshListFinancialTransaction();
          refreshListFinancialCashFlow();
          refreshListFinancialCosting();
          notification.success({
            message: translation.financial.messages.deleteSuccess,
          });
        } catch (error) {
          notification.error({
            message: translation.financial.messages.deleteError,
          });
        } finally {
          setIsLoadingRequest(false);
        }
      }

      Modal.confirm({
        title: translation.financial.messages.deleteTransactionConfirm,
        content: `Documento: ${record.documentNumber} - Fornecedor/Cliente: ${record.entityName}`,
        centered: true,
        okText: translation.buttons.yes,
        onOk: () => {
          executeDelete(id, false);
        },
        cancelText: translation.buttons.no,
      });
    },
    [
      farmId,
      groupId,
      refreshListFinancialCashFlow,
      refreshListFinancialCosting,
      refreshListFinancialTransaction,
      translation.buttons.no,
      translation.buttons.yes,
      translation.financial.messages.deleteError,
      translation.financial.messages.deleteSuccess,
      translation.financial.messages.deleteTransactionConfirm,
    ],
  );

  const handleEdit = useCallback(
    (data) => {
      let installments = [];
      if (data.installmentTotal > 1) {
        installments = data.installments.map((installment) => ({
          date: installment.expirationDate,
          value: installment.value,
        }));
      }
      const transactionData = {
        ...data,
        financialNatureId: data.financialNatureId,
        financialClassId: data.financialClassId,
        mainValueCurrency: data.mainValueCurrency,
        mainValue: data.mainValue,
        payment: data.installmentTotal > 1 ? "part" : "inCash",
        resultCenterApportionmentDefault:
          data.resultCenterApportionmentDefault === false ? null : true,
        action: data.isSettled ? "launchAndSettle" : "launch",
        installments,
      };
      handleSetFinancialTransactionDataToEdit(transactionData);
      if (shouldShowFormFinancialTransaction === false) {
        openOrCloseDrawerFinancialTransactionForm();
      }
    },
    [
      handleSetFinancialTransactionDataToEdit,
      shouldShowFormFinancialTransaction,
      openOrCloseDrawerFinancialTransactionForm,
    ],
  );

  const handleGetDateSearchProps = useCallback(
    (dataIndex) => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div
          style={{
            padding: 8,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Row style={{ marginBottom: 5 }}>
            <DatePicker.RangePicker
              value={selectedKeys && selectedKeys[0]?.dates}
              format="DD/MM/YYYY"
              onChange={(dates, dateStrings) => setSelectedKeys([{ dates }])}
            />
          </Row>
          <Row>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm)}
              icon="search"
              size="small"
              style={{
                width: 90,
                marginRight: 8,
                background: "#684e94",
                borderColor: "none",
                border: "none",
              }}
            />
            <Button
              onClick={() => handleReset(clearFilters)}
              icon="delete"
              size="small"
              style={{ width: 90 }}
            />
          </Row>
        </div>
      ),
      filterIcon: (filtered) => (
        <Icon
          type="calendar"
          style={{ color: filtered ? "#684e94" : undefined }}
        />
      ),
      onFilter: (value, record) => {
        return moment(record[dataIndex]).isBetween(
          value?.dates[0],
          value?.dates[1],
          "days",
          "[]",
        );
      },
    }),
    [handleReset, handleSearch],
  );

  const handleGetColumnValueSearchProps = useCallback(
    (dataIndex) => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <div style={{ display: "block" }}>
            <Select
              value={
                selectedKeys
                  ? selectedKeys[0]?.type != null
                    ? selectedKeys[0]?.type
                    : "="
                  : "="
              }
              style={{
                width: 90,
                textAlign: "center",
                marginBottom: 8,
                marginRight: 8,
              }}
              onChange={(value) => {
                setSelectedKeys(
                  value
                    ? [{ ...selectedKeys[0], type: value }]
                    : [{ ...selectedKeys[0] }],
                );
              }}
            >
              <Select.Option key="<" value="<" style={{ textAlign: "center" }}>
                &lt;
              </Select.Option>
              <Select.Option
                key="<="
                value="<="
                style={{ textAlign: "center" }}
              >
                &le;
              </Select.Option>
              <Select.Option key="=" value="=" style={{ textAlign: "center" }}>
                &#61;
              </Select.Option>
              <Select.Option
                key=">="
                value=">="
                style={{ textAlign: "center" }}
              >
                &ge;
              </Select.Option>
              <Select.Option key=">" value=">" style={{ textAlign: "center" }}>
                &gt;
              </Select.Option>
            </Select>
            <InputNumber
              value={
                selectedKeys
                  ? selectedKeys[0]?.value != null
                    ? selectedKeys[0]?.value
                    : null
                  : null
              }
              onChange={(value) =>
                setSelectedKeys(
                  value
                    ? [{ ...selectedKeys[0], value: value }]
                    : [{ ...selectedKeys[0] }],
                )
              }
              onPressEnter={() => handleSearch(selectedKeys, confirm)}
              style={{ width: 90, marginBottom: 8 }}
              defaultValue={0}
              decimalSeparator=","
              min={-1000}
              max={1000}
              step={0.01}
              formatter={(value) => {
                if (value !== "") {
                  value = `${value}`.replace(/^-^[^0-9.,]*/g, "");
                  value = value.replace(/\.{2,}/g, ".");
                  value = value.replace(/\.,/g, ",");
                  value = value.replace(/,\./g, ",");
                  value = value.replace(/,{2,}/g, ",");
                  value = value.replace(/\.[0-9]+\./g, ".");
                  value =
                    value.split(".")[1] !== "" &&
                    value.split(".")[1] !== undefined
                      ? value.split(".")[0] +
                        "." +
                        value.split(".")[1].substring(0, 2)
                      : value;
                }
                return value;
              }}
            />
          </div>
          <div>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm)}
              icon="search"
              size="small"
              style={{
                width: 90,
                marginRight: 8,
                background: "#684e94",
                borderColor: "none",
                border: "none",
              }}
            />
            <Button
              onClick={() => handleReset(clearFilters)}
              icon="delete"
              size="small"
              style={{ width: 90 }}
            />
          </div>
        </div>
      ),
      filterIcon: (filtered) => (
        <Icon
          type="search"
          style={{ color: filtered ? "#684e94" : undefined }}
        />
      ),
      onFilter: (value, record) => {
        switch (value.type) {
          case "=":
            return getTwoDecimalDigits(record[dataIndex]) === value.value;
          case "<=":
            return getTwoDecimalDigits(record[dataIndex]) <= value.value;
          case "<":
            return getTwoDecimalDigits(record[dataIndex]) < value.value;
          case ">=":
            return getTwoDecimalDigits(record[dataIndex]) >= value.value;
          case ">":
            return getTwoDecimalDigits(record[dataIndex]) > value.value;
          default:
            return true;
        }
      },
    }),
    [handleReset, handleSearch],
  );

  const handleCloseIssue = useCallback(
    async (data) => {
      try {
        await updateFinancialTransactionIssueToClose({
          groupId,
          farmId,
          id: data.id,
        });
        refreshListFinancialTransaction();
        refreshListFinancialCashFlow();
        refreshListFinancialCosting();
        notification.success({
          message: translation.financial.messages.issueResolved,
        });
      } catch (err) {}
    },
    [
      farmId,
      groupId,
      refreshListFinancialCashFlow,
      refreshListFinancialCosting,
      refreshListFinancialTransaction,
      translation.financial.messages.issueResolved,
    ],
  );

  const handleReopenIssue = useCallback(
    async (data) => {
      try {
        await updateFinancialTransactionIssueToOpen({
          groupId,
          farmId,
          id: data.id,
        });
        refreshListFinancialTransaction();
        refreshListFinancialCashFlow();
        refreshListFinancialCosting();
        notification.success({
          message: translation.financial.messages.issueReopened,
        });
      } catch (err) {}
    },
    [
      farmId,
      groupId,
      refreshListFinancialCashFlow,
      refreshListFinancialCosting,
      refreshListFinancialTransaction,
      translation.financial.messages.issueReopened,
    ],
  );

  const handlePartialPayment = useCallback(
    async (id, record) => {
      handleOpenModalPartialPayment(record);
    },
    [handleOpenModalPartialPayment],
  );

  const handleShowModalToExport = () => {
    dispatch(AppActions.showModalExportTransactions());
  };

  const handleClickButtonSettleTransaction = useCallback(() => {
    handleOpenModalSettled("new");
  }, [handleOpenModalSettled]);

  const handleClickButtonRemoveSettleTransaction = useCallback(() => {
    handleOpenModalSettled("remove");
  }, [handleOpenModalSettled]);

  const handleDownloadInvoice = useCallback(
    async (invoiceId) => {
      const response = await downloadInvoiceFile({
        groupId,
        farmId,
        invoiceId,
      });
      const filename = filenameFromDisposition(
        response.headers["content-disposition"], // o Axios entrega os headers em minúsculas
        `nota-fiscal-${invoiceId}`,
      );

      const objectUrl = URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(objectUrl);
    },
    [groupId, farmId],
  );

  const menu = useCallback(
    (id, record) => {
      return (
        <Menu>
          {!record.isSettled && (
            <Menu.Item key="3">
              <button onClick={() => handlePartialPayment(id, record)}>
                {translation.financial.table.partialPayment}
              </button>
            </Menu.Item>
          )}
        </Menu>
      );
    },
    [handlePartialPayment, translation.financial.table.partialPayment],
  );

  const menuMain = (id, record) => {
    return (
      <Menu>
        <Menu.Item key="1">
          <button onClick={() => handleDelete(id, record)}>
            {translation.table.menu.delete}
          </button>
        </Menu.Item>
        {!CLASS_ID_WITHOUT_EDITION.includes(record.financialClassId) && (
          <Menu.Item key="2">
            <button onClick={() => handleEdit(record)}>
              {translation.table.menu.edit}
            </button>
          </Menu.Item>
        )}
        {record.financialIssueId && !record.financialIssueResolved === true && (
          <Menu.Item key="2">
            <button onClick={() => handleCloseIssue(record)}>
              {translation.financial.table.issueResolved}
            </button>
          </Menu.Item>
        )}
        {record.financialIssueId && record.financialIssueResolved === true && (
          <Menu.Item key="2">
            <button onClick={() => handleReopenIssue(record)}>
              {translation.financial.table.issueReopen}
            </button>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const expandRowsInstallmentsApportioment = useCallback(
    (record, index, indent, expanded) => {
      const dataSource = record.transactions;
      return (
        <Table
          title={() => (
            <Row type="flex" justify="center" align="middle">
              <h2>{translation.financial.table.rateio}</h2>
            </Row>
          )}
          rowKey="id"
          size="small"
          dataSource={dataSource || []}
          pagination={false}
          scroll={{
            x: true,
          }}
          style={{ width: "100%", margin: "0" }}
        >
          <Column
            title={
              translation.financial.transactions.table.columns.costActivityType
            }
            dataIndex="costActivityType"
            key="costActivityType"
            align="left"
            sorter={(first, second) => {
              return first.costActivityType.localeCompare(
                second.costActivityType,
              );
            }}
            filtered
            filters={[
              {
                text: "Cria",
                value: "Growth",
              },
              {
                text: "Extensivo",
                value: "RecreateFattenPasture",
              },
              {
                text: "Semi-Intensivo",
                value: "RecreateFattenSemiConfinement",
              },
              {
                text: "Intensivo",
                value: "RecreateFattenConfinement",
              },
              {
                text: "Agricultura",
                value: "Agriculture",
              },
              {
                text: "Outros",
                value: "Others",
              },
            ]}
            filterMultiple
            onFilter={(value, record) => {
              return record.costActivityType.includes(value);
            }}
            render={(text, record) => (
              <span>
                {record?.costActivityType
                  ? translation.financial.costType[record.costActivityType]
                  : ""}
              </span>
            )}
          />
          <Column
            title={translation.financial.transactions.table.columns.value}
            dataIndex="value"
            key="value"
            align="left"
            sorter={(first, second) => {
              return first.value - second.value;
            }}
            render={(text, record) => (
              <span>
                {numberMask(record.value || 0, true, record.valueCurrency)}
              </span>
            )}
          />
          <Column
            title={
              translation.financial.transactions.table.columns
                .financialClassDepreciation
            }
            dataIndex="financialClassDepreciation"
            key="financialClassDepreciation"
            align="left"
            sorter={(first, second) => {
              return (
                first.financialClassDepreciation -
                second.financialClassDepreciation
              );
            }}
            render={(text) => <span>{`${text || 0} meses`}</span>}
          />
          <Column
            title={
              translation.financial.transactions.table.columns.valueDepreciation
            }
            dataIndex="valueDepreciation"
            key="valueDepreciation"
            align="left"
            sorter={(first, second) => {
              return first.valueDepreciation - second.valueDepreciation;
            }}
            render={(text, record) => (
              <span>
                {numberMask(
                  record.valueDepreciation || 0,
                  true,
                  record.valueCurrency,
                )}
              </span>
            )}
          />
        </Table>
      );
    },
    [
      translation.financial.costType,
      translation.financial.table.rateio,
      translation.financial.transactions.table.columns.costActivityType,
      translation.financial.transactions.table.columns
        .financialClassDepreciation,
      translation.financial.transactions.table.columns.value,
      translation.financial.transactions.table.columns.valueDepreciation,
    ],
  );

  const expandRowsInstallments = useCallback(
    (record, index, indent, expanded) => {
      const dataSource = record.installments;
      return (
        <Table
          title={() => (
            <Row type="flex" justify="center" align="middle">
              <h2>{translation.financial.table.parcelas}</h2>
            </Row>
          )}
          rowKey="id"
          size="small"
          dataSource={dataSource || []}
          pagination={false}
          scroll={{
            x: true,
          }}
          style={{ width: "100%", margin: "0" }}
          expandedRowRender={expandRowsInstallmentsApportioment}
        >
          <Column
            title={translation.financial.transactions.table.columns.installment}
            dataIndex="installmentNumber"
            key="installmentNumber"
            align="left"
            sorter={(first, second) => {
              return first.installmentNumber - second.installmentNumber;
            }}
            render={(text, record) => (
              <span>
                {`${record.installmentNumber}/${record.installmentTotal} `}
                {record.updatedOnSettle && (
                  <InfoTooltip title={"Parcela atualizada na baixa"} />
                )}
              </span>
            )}
          />
          <Column
            title={
              translation.financial.transactions.table.columns.expirationDate
            }
            dataIndex="expirationDate"
            key="expirationDate"
            align="left"
            sorter={(first, second) => {
              return moment(first.expirationDate).isBefore(
                moment(second.expirationDate),
              );
            }}
            {...handleGetDateSearchProps("expirationDate")}
            render={(text) => <span>{moment(text).format("DD/MM/YYYY")}</span>}
          />

          <Column
            title={
              translation.financial.transactions.table.columns.isPaidPartially
            }
            dataIndex="isPaidPartially"
            key="isPaidPartially"
            align="left"
            sorter={(first, second) => {
              return first.isPaidPartially === second.isPaidPartially
                ? 0
                : first.isPaidPartially
                  ? -1
                  : 1;
            }}
            filtered
            filters={[
              {
                text: "Todos",
                value: "all",
              },
              {
                text: "Parc. Pago",
                value: "true",
              },
              {
                text: "Pago",
                value: "false",
              },
            ]}
            filterMultiple={false}
            onFilter={(value, record) => {
              return value === "all"
                ? true
                : value === "true"
                  ? record.isPaidPartially === true
                  : record.isPaidPartially === false;
            }}
            render={(text, record) =>
              text === true ? <Icon type="close" /> : ""
            }
          />

          <Column
            title={translation.financial.transactions.table.columns.isSettled}
            dataIndex="isSettled"
            key="isSettled"
            align="left"
            sorter={(first, second) => {
              return first.isSettled === second.isSettled
                ? 0
                : first.isSettled
                  ? -1
                  : 1;
            }}
            filtered
            filters={[
              {
                text: "Todos",
                value: "all",
              },
              {
                text: "Baixado",
                value: "true",
              },
              {
                text: "Em Aberto",
                value: "false",
              },
            ]}
            filterMultiple={false}
            onFilter={(value, record) => {
              return value === "all"
                ? true
                : value === "true"
                  ? record.isSettled === true
                  : record.isSettled === false;
            }}
            render={(text, record) =>
              record.isSettled === false ? (
                <TagStatus
                  background="#E4F4FF"
                  borderColor="#4A85AE"
                  color="#4A85AE"
                >
                  {translation.financial.table.tagOpen}
                </TagStatus>
              ) : (
                <TagStatus
                  background="#E1FFE4"
                  borderColor="#106518"
                  color="#106518"
                >
                  {translation.financial.table.tagSettled}
                </TagStatus>
              )
            }
          />
          <Column
            title={translation.financial.transactions.table.columns.settleDate}
            dataIndex="settleDate"
            key="settleDate"
            align="left"
            sorter={(first, second) => {
              return moment(first.settleDate).isBefore(
                moment(second.settleDate),
              );
            }}
            {...handleGetDateSearchProps("settleDate")}
            render={(text) => (text ? moment(text).format("DD/MM/YYYY") : null)}
          />
          <Column
            title={
              translation.financial.transactions.table.columns
                .financialPaymentMethodId
            }
            dataIndex="financialPaymentMethodName"
            key="financialPaymentMethodName"
            align="left"
            sorter={(first, second) => {
              return (
                first.financialPaymentMethodName -
                second.financialPaymentMethodName
              );
            }}
          />
          <Column
            title={
              translation.financial.transactions.table.columns.bankAccountFarmId
            }
            dataIndex="bankAccountFarmName"
            key="bankAccountFarmName"
            align="left"
            sorter={(first, second) => {
              return first.bankAccountFarmName - second.bankAccountFarmName;
            }}
          />
          <Column
            title={translation.financial.transactions.table.columns.fees}
            dataIndex="fees"
            key="fees"
            align="left"
            sorter={(first, second) => {
              return first.fees - second.fees;
            }}
            {...handleGetColumnValueSearchProps("fees")}
            render={(text, record) => (
              <span>
                {numberMask(record.fees || 0, true, record.valueCurrency)}
              </span>
            )}
          />
          <Column
            title={translation.financial.transactions.table.columns.discount}
            dataIndex="discount"
            key="discount"
            align="left"
            sorter={(first, second) => {
              return first.discount - second.discount;
            }}
            {...handleGetColumnValueSearchProps("discount")}
            render={(text, record) => (
              <span>
                {numberMask(record.discount || 0, true, record.valueCurrency)}
              </span>
            )}
          />
          <Column
            title={translation.financial.transactions.table.columns.value}
            dataIndex="value"
            key="value"
            align="left"
            sorter={(first, second) => {
              return first.value - second.value;
            }}
            {...handleGetColumnValueSearchProps("value")}
            render={(text, record) => (
              <span>
                {numberMask(record.value || 0, true, record.valueCurrency)}
              </span>
            )}
          />
          <Column
            title={translation.financial.table.valuePaid}
            dataIndex="valuePaid"
            key="valuePaid"
            align="left"
            sorter={(first, second) => {
              return first.valuePaid - second.valuePaid;
            }}
            {...handleGetColumnValueSearchProps("valuePaid")}
            render={(text, record) => (
              <span>
                {numberMask(record.valuePaid || 0, true, record.valueCurrency)}
              </span>
            )}
          />
          <Column
            title={translation.financial.table.remainingValue}
            dataIndex="remainingValue"
            key="remainingValue"
            align="left"
            sorter={(first, second) => {
              return first.remainingValue - second.remainingValue;
            }}
            {...handleGetColumnValueSearchProps("remainingValue")}
            render={(text, record) => (
              <span>{numberMask(record.remainingValue || 0, true)}</span>
            )}
          />
          <Column
            align="left"
            render={(text, record) => (
              <Dropdown
                overlay={menu(record.id, record)}
                trigger={["click"]}
                key={record.id}
              >
                <MenuContainer>
                  <MenuIcon />
                </MenuContainer>
              </Dropdown>
            )}
          />
        </Table>
      );
    },
    [
      expandRowsInstallmentsApportioment,
      translation.financial.transactions.table.columns.installment,
      translation.financial.transactions.table.columns.expirationDate,
      translation.financial.transactions.table.columns.isPaidPartially,
      translation.financial.transactions.table.columns.isSettled,
      translation.financial.transactions.table.columns.settleDate,
      translation.financial.transactions.table.columns.financialPaymentMethodId,
      translation.financial.transactions.table.columns.bankAccountFarmId,
      translation.financial.transactions.table.columns.fees,
      translation.financial.transactions.table.columns.discount,
      translation.financial.transactions.table.columns.value,
      translation.financial.table.valuePaid,
      translation.financial.table.remainingValue,
      translation.financial.table.parcelas,
      translation.financial.table.tagOpen,
      translation.financial.table.tagSettled,
      handleGetDateSearchProps,
      handleGetColumnValueSearchProps,
      menu,
    ],
  );

  const TableListOfFinancialTransactionsMain = (
    <Table
      rowClassName={(record) =>
        record.transactionType === "expense"
          ? "table-row-red"
          : "table-row-green"
      }
      loading={isLoading || isLoadingRequest}
      rowKey="id"
      size="small"
      dataSource={data.content ?? []}
      pagination={{
        total: data != null ? data.totalElements : 0,
        size: data != null ? size : 0,
        current: data != null ? page : 0,
        hideOnSinglePage:
          data !== null && Object.entries(data).length !== 0
            ? data.totalElements > 10
              ? false
              : true
            : true,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "100", "500", "1000", "2000"],
      }}
      scroll={{
        x: true,
      }}
      style={{ width: "100%", margin: "0" }}
      expandedRowRender={expandRowsInstallments}
      onChange={handleTableChange}
    >
      <Column
        title={translation.financial.transactions.table.columns.origin}
        dataIndex="origin"
        sorter
        sortOrder={tableSorters?.columnKey === "origin" && tableSorters?.order}
        filteredValue={tableFilters?.origin || null}
        key="origin"
        align="left"
        filtered
        filters={[
          {
            text: "BovExo",
            value: "internal",
          },
          {
            text: "Externo",
            value: "third_applications",
          },
          {
            text: "Conversão",
            value: "conversion",
          },
        ]}
        render={(text) =>
          text === "internal" ? (
            <TagStatus
              background="#684e75"
              borderColor="#684e94"
              color="#FFFFFF"
            >
              BovExo
            </TagStatus>
          ) : text === "third_applications" ? (
            <TagStatus
              background="#E3E3E3"
              borderColor="#C4C4C4"
              color="#4B4B4B"
            >
              {translation.financial.table.tagExternal}
            </TagStatus>
          ) : (
            <TagStatus
              background="#E3E3E3"
              borderColor="#C4C4C4"
              color="#4B4B4B"
            >
              {translation.financial.table.tagConversion}
            </TagStatus>
          )
        }
      />
      <Column
        title={translation.financial.transactions.table.columns.transactionType}
        dataIndex="transactionType"
        sorter
        sortOrder={
          tableSorters?.columnKey === "transactionType" && tableSorters?.order
        }
        filteredValue={tableFilters?.transactionType || null}
        key="transactionType"
        align="left"
        filtered
        filters={[
          {
            text: "Todos",
            value: "all",
          },
          {
            text: "Despesa",
            value: "expense",
          },
          {
            text: "Receita",
            value: "income",
          },
        ]}
        filterMultiple={false}
        render={(text) =>
          text === "expense" ? <span>D</span> : <span>R</span>
        }
      />
      <Column
        title={translation.financial.transactions.table.columns.documentNumber}
        dataIndex="documentNumber"
        sorter
        sortOrder={
          tableSorters?.columnKey === "documentNumber" && tableSorters?.order
        }
        filteredValue={tableFilters?.documentNumber || null}
        key="documentNumber"
        align="left"
        width={200}
        {...getColumnSearchProps("documentNumber", handleReset, handleSearch)}
        render={(text, record) => record.documentNumberAlias}
      />
      {isEnableFinancialTaxableDeductible && (
        <Column
          title={
            translation.financial.transactions.table.columns
              .financialTaxableDeductibleId
          }
          dataIndex="financialTaxableDeductibleName"
          sorter
          sortOrder={
            tableSorters?.financialTaxablcolumnKey ===
              "financialTaxableDeductibleName" && tableSorters?.order
          }
          filteredValue={tableFilters?.financialTaxableDeductibleName || null}
          key="financialTaxableDeductibleName"
          align="left"
          width={250}
          {...getColumnSearchProps(
            "financialTaxableDeductibleName",
            handleReset,
            handleSearch,
          )}
        />
      )}
      <Column
        title={translation.financial.transactions.table.columns.entityName}
        dataIndex="entityName"
        sorter
        sortOrder={
          tableSorters?.columnKey === "entityName" && tableSorters?.order
        }
        filteredValue={tableFilters?.entityName || null}
        key="entityName"
        align="left"
        {...getColumnSearchProps("entityName", handleReset, handleSearch)}
      />
      <Column
        title={translation.financial.transactions.table.columns.billingDate}
        dataIndex="billingDate"
        sorter
        sortOrder={
          tableSorters?.columnKey === "billingDate" && tableSorters?.order
        }
        filteredValue={tableFilters?.billingDate || null}
        key="billingDate"
        align="left"
        {...getColumnDateWithBetweenSearchProps(
          "billingDate",
          handleReset,
          handleSearch,
        )}
        render={(text) => <span>{moment(text).format("DD/MM/YYYY")}</span>}
      />
      <Column
        title={translation.financial.transactions.table.columns.expirationDate}
        dataIndex="expirationDate"
        sorter
        sortOrder={
          tableSorters?.columnKey === "expirationDate" && tableSorters?.order
        }
        filteredValue={tableFilters?.expirationDate || null}
        key="expirationDate"
        align="left"
        {...getColumnDateWithBetweenSearchProps(
          "expirationDate",
          handleReset,
          handleSearch,
        )}
        render={(text) => <span>{moment(text).format("DD/MM/YYYY")}</span>}
      />
      <Column
        title={translation.financial.transactions.table.columns.isSettled}
        dataIndex="status"
        width={150}
        filteredValue={tableFilters?.status || null}
        key="status"
        align="left"
        filtered
        filters={[
          {
            text: "Todos",
            value: "all",
          },
          {
            text: "Baixado",
            value: "true",
          },
          {
            text: "Em Aberto",
            value: "false",
          },
          {
            text: "Conciliado",
            value: "conciliated",
          },
          {
            text: "Parc. Conciliado",
            value: "partial_conciliated",
          },
        ]}
        filterMultiple={false}
        render={(text, record) =>
          record.isSettled === false &&
          record.conciliationStatus === "not_conciliated" ? (
            <TagStatus
              background="#E4F4FF"
              borderColor="#4A85AE"
              color="#4A85AE"
            >
              {translation.financial.table.tagOpen}
            </TagStatus>
          ) : record.conciliationStatus === "conciliated" ? (
            <TagStatus
              background="#E1FFE4"
              borderColor="#106518"
              color="#106518"
            >
              {translation.financial.table.tagConciliated}
            </TagStatus>
          ) : record.conciliationStatus === "partial_conciliated" ? (
            <TagStatus
              background="#FFDBBC"
              borderColor="#FE8D2A"
              color="#FE8D2A"
            >
              {translation.financial.table.tagPartialConciliated}
            </TagStatus>
          ) : (
            <TagStatus
              background="#E1FFE4"
              borderColor="#106518"
              color="#106518"
            >
              {translation.financial.table.tagSettled}
            </TagStatus>
          )
        }
      />
      <Column
        title={translation.financial.transactions.table.columns.value}
        dataIndex="value"
        sorter
        sortOrder={tableSorters?.columnKey === "value" && tableSorters?.order}
        filteredValue={tableFilters?.value || null}
        key="value"
        align="left"
        render={(text, record) => (
          <span>
            {numberMask(record.value || 0, true, record.valueCurrency)}
          </span>
        )}
        {...getColumnNumberWithBetweenSearchProps(
          "value",
          handleReset,
          handleSearch,
        )}
      />
      {isEnablePaymentMethod && (
        <Column
          title={
            translation.financial.transactions.table.columns
              .financialPaymentMethodId
          }
          dataIndex="financialPaymentMethodName"
          sorter
          sortOrder={
            tableSorters?.columnKey === "financialPaymentMethodName" &&
            tableSorters?.order
          }
          filteredValue={tableFilters?.financialPaymentMethodName || null}
          key="financialPaymentMethodName"
          align="left"
          {...getColumnSearchProps(
            "financialPaymentMethodName",
            handleReset,
            handleSearch,
          )}
        />
      )}
      <Column
        title={translation.financial.transactions.table.columns.nature}
        dataIndex="financialNatureName"
        sorter
        sortOrder={
          tableSorters?.columnKey === "financialNatureName" &&
          tableSorters?.order
        }
        filteredValue={tableFilters?.financialNatureName || null}
        key="financialNatureName"
        align="left"
        width={180}
        {...getColumnSearchProps(
          "financialNatureName",
          handleReset,
          handleSearch,
        )}
      />
      <Column
        title={translation.financial.transactions.table.columns.class}
        dataIndex="financialClassName"
        sorter
        sortOrder={
          tableSorters?.columnKey === "financialClassName" &&
          tableSorters?.order
        }
        filteredValue={tableFilters?.financialClassName || null}
        key="financialClassName"
        align="left"
        width={180}
        {...getColumnSearchProps(
          "financialClassName",
          handleReset,
          handleSearch,
        )}
      />
      <Column
        title={
          translation.financial.transactions.table.columns.financialProjectName
        }
        dataIndex="financialProjectName"
        sorter
        sortOrder={
          tableSorters?.columnKey === "financialProjectName" &&
          tableSorters?.order
        }
        filteredValue={tableFilters?.financialProjectName || null}
        key="financialProjectName"
        align="left"
        {...getColumnSearchProps(
          "financialProjectName",
          handleReset,
          handleSearch,
        )}
      />
      {isEnableBankAccountFarm && (
        <Column
          title={
            translation.financial.transactions.table.columns.bankAccountFarmId
          }
          dataIndex="bankAccountFarmName"
          sorter
          sortOrder={
            tableSorters?.columnKey === "bankAccountFarmName" &&
            tableSorters?.order
          }
          filteredValue={tableFilters?.bankAccountFarmName || null}
          key="bankAccountFarmName"
          align="left"
          {...getColumnSearchProps(
            "bankAccountFarmName",
            handleReset,
            handleSearch,
          )}
        />
      )}
      {isEnableFinancialIssue && (
        <Column
          title={
            translation.financial.transactions.table.columns.financialIssueId
          }
          dataIndex="financialIssueResolved"
          sorter
          sortOrder={
            tableSorters?.columnKey === "financialIssueResolved" &&
            tableSorters?.order
          }
          filteredValue={tableFilters?.financialIssueResolved || null}
          key="financialIssueResolved"
          align="left"
          filtered
          filters={[
            {
              text: "R",
              value: true,
            },
            {
              text: "P",
              value: false,
            },
            {
              text: "Vazio",
              value: "null",
            },
          ]}
          render={(text, record) =>
            record.financialIssueResolved !== null &&
            record.financialIssueResolved === true
              ? "R"
              : record.financialIssueResolved !== null &&
                  record.financialIssueResolved === false
                ? "P"
                : "-"
          }
        />
      )}
      <Column
        title={translation.financial.transactions.table.columns.r}
        dataIndex="resultCenterApportionmentDefault"
        sorter
        sortOrder={
          tableSorters?.columnKey === "resultCenterApportionmentDefault" &&
          tableSorters?.order
        }
        filteredValue={tableFilters?.resultCenterApportionmentDefault || null}
        key="resultCenterApportionmentDefault"
        align="left"
        render={(text, record) => (
          <span>
            {record?.resultCenterApportionmentDefault === true ? "P" : "R"}
          </span>
        )}
      />
      <Column
        title={translation.financial.transactions.table.columns.invoiceUrl}
        dataIndex="invoiceUrl"
        key="invoiceUrl"
        align="left"
        render={(text, record) =>
          record.invoiceUrl ? (
            <Button
              type="link"
              onClick={() => handleDownloadInvoice(record.invoiceId)}
            >
              <Icon type="file" />
            </Button>
          ) : (
            "-"
          )
        }
      />
      <Column
        align="left"
        render={(text, record) => (
          <Dropdown
            overlay={menuMain(record.id, record)}
            trigger={["click"]}
            key={record.id}
          >
            <MenuContainer>
              <MenuIcon />
            </MenuContainer>
          </Dropdown>
        )}
      />
    </Table>
  );

  useEffect(() => {
    let doFetch = true;
    async function getParameters() {
      let dataResult = [];
      try {
        for (var i = 0; i < CODES_PARAMETERS.length; i++) {
          let code = CODES_PARAMETERS[i];
          let {
            data: { results },
          } = await getParameterItems({ groupId, farmId, code });
          results.isDefault = results.farmId == null && results.groupId == null;
          results.updated = false;
          dataResult.push(results);
        }
        setParametersValue(dataResult);
      } catch (error) {
      } finally {
      }
    }
    if (doFetch) getParameters();

    return () => {
      doFetch = false;
    };
  }, [farmId, groupId]);

  return (
    <CardCustom>
      <Row type="flex" justify="space-between" className="rowHeader">
        <Col>
          <Title>{translation.financial.transactions.title}</Title>
        </Col>
        <Col>
          <Row type="flex">
            <div className="colButtons">
              <ButtonStandard
                buttonType="typeWithoutBackground"
                disabled={isLoading}
                onClick={() =>
                  refreshListFinancialTransaction(
                    page,
                    size,
                    tableSorters,
                    search,
                  )
                }
              >
                <Icon type="reload" />
              </ButtonStandard>
            </div>
            <div className="colButtons">
              <ButtonStandard
                name="buttonExportTransactions"
                buttonType="alternative"
                onClick={handleShowModalToExport}
              >
                {translation.financial.transactions.buttonExportTransactions}
              </ButtonStandard>
            </div>
            {!shouldShowFormFinancialTransaction && (
              <div className="colButtons">
                <ButtonStandard
                  name="buttonCreateBudget"
                  buttonType="addNew"
                  onClick={openOrCloseDrawerFinancialTransactionForm}
                >
                  {
                    translation.financial.transactions
                      .buttonCreateNewFinancialTransaction
                  }
                </ButtonStandard>
              </div>
            )}
          </Row>
        </Col>
      </Row>
      <Row type="flex" className="rowForm">
        <Col span={24}>
          {shouldShowFormFinancialTransaction && (
            <NewFinancialTransactionsForm />
          )}
        </Col>
      </Row>
      <Row type="flex" gutter={8} style={{ marginBottom: 8 }}>
        <Col>
          <Tooltip title={translation.financial.table.tooltipSettle}>
            <div>
              <ButtonStandard
                buttonType="principal"
                style={{ fontSize: 16 }}
                onClick={handleClickButtonSettleTransaction}
              >
                <FaCashRegister size={16} />
                {translation.financial.table.settle}
              </ButtonStandard>
            </div>
          </Tooltip>
        </Col>
        <Col>
          <Tooltip title={translation.financial.table.tooltipRemoveSettle}>
            <div>
              <ButtonStandard
                buttonType="cancel"
                style={{ fontSize: 16 }}
                onClick={handleClickButtonRemoveSettleTransaction}
              >
                <FiDelete size={16} />
                {translation.financial.table.removeSettle}
              </ButtonStandard>
            </div>
          </Tooltip>
        </Col>
      </Row>
      <Row type="flex" justify="space-between">
        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          {TableListOfFinancialTransactionsMain}
        </Col>
      </Row>
    </CardCustom>
  );
};

export default FinancialTransactions;
